import * as React from 'react';

/**
 * Minimal inline glyphs for the support desk chrome (app-bar tabs, pane-1 views, list header).
 * These are stroke="currentColor" outlines lifted from the Claude Design prototype; colour is
 * driven by the consuming element's `color`, so they tint with the active/inactive state.
 *
 * Kept inline (rather than added to web/components/svgs/icons) because they are desk-local chrome
 * and small; promote to shared SVG components if reused outside this page.
 */

type GlyphProps = { className?: string; size?: number };
type StrokeGlyphProps = GlyphProps & { strokeWidth?: number };

const stroke = (size: number, className: string | undefined, path: React.ReactNode, strokeWidth = 2) => (
	<svg
		width={size}
		height={size}
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth={strokeWidth}
		strokeLinecap='round'
		strokeLinejoin='round'
		className={className}
		aria-hidden='true'
	>
		{path}
	</svg>
);

export const InboxGlyph: React.FC<GlyphProps> = ({ className, size = 16 }) =>
	stroke(
		size,
		className,
		<>
			<path d='M22 12h-6l-2 3h-4l-2-3H2' />
			<path d='M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z' />
		</>
	);

export const ReportsGlyph: React.FC<GlyphProps> = ({ className, size = 16 }) =>
	stroke(
		size,
		className,
		<>
			<path d='M3 3v18h18' />
			<rect x='7' y='11' width='3' height='6' />
			<rect x='12' y='7' width='3' height='10' />
			<rect x='17' y='13' width='3' height='4' />
		</>
	);

export const SettingsGlyph: React.FC<GlyphProps> = ({ className, size = 16 }) =>
	stroke(
		size,
		className,
		<>
			<line x1='4' y1='6' x2='20' y2='6' />
			<line x1='4' y1='12' x2='20' y2='12' />
			<line x1='4' y1='18' x2='20' y2='18' />
			<circle cx='9' cy='6' r='2' />
			<circle cx='15' cy='12' r='2' />
			<circle cx='8' cy='18' r='2' />
		</>
	);

export const BellGlyph: React.FC<GlyphProps> = ({ className, size = 16 }) =>
	stroke(
		size,
		className,
		<>
			<path d='M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9' />
			<path d='M10.3 21a1.94 1.94 0 0 0 3.4 0' />
		</>
	);

export const UnassignedGlyph: React.FC<GlyphProps> = ({ className, size = 16 }) =>
	stroke(
		size,
		className,
		<>
			<circle cx='12' cy='8' r='4' />
			<path d='M4 21a8 8 0 0 1 16 0' />
		</>
	);

export const PersonGlyph: React.FC<GlyphProps> = ({ className, size = 16 }) =>
	stroke(
		size,
		className,
		<>
			<circle cx='12' cy='7' r='4' />
			<path d='M5.5 21a6.5 6.5 0 0 1 13 0' />
		</>
	);

export const PeopleGlyph: React.FC<GlyphProps> = ({ className, size = 16 }) =>
	stroke(
		size,
		className,
		<>
			<path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' />
			<circle cx='9' cy='7' r='4' />
			<path d='M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75' />
		</>
	);

export const CheckGlyph: React.FC<GlyphProps> = ({ className, size = 16 }) =>
	stroke(size, className, <path d='M20 6 9 17l-5-5' />);

export const SnoozeGlyph: React.FC<GlyphProps> = ({ className, size = 16 }) =>
	stroke(
		size,
		className,
		<>
			<circle cx='12' cy='13' r='8' />
			<path d='M12 9v4l2 2' />
			<path d='M5 3 2 6' />
			<path d='m22 6-3-3' />
		</>
	);

export const ReopenGlyph: React.FC<GlyphProps> = ({ className, size = 16 }) =>
	stroke(
		size,
		className,
		<>
			<path d='M3 12a9 9 0 0 1 15.5-6.2' />
			<path d='M18.5 2v3.8h-3.8' />
			<path d='M21 12a9 9 0 0 1-15.5 6.2' />
			<path d='M5.5 22v-3.8h3.8' />
		</>
	);

export const RefreshGlyph: React.FC<GlyphProps> = ({ className, size = 14 }) =>
	stroke(
		size,
		className,
		<>
			<path d='M20.5 12a8.5 8.5 0 1 1-2.8-6.3' />
			<path d='M20.8 3.5v4h-4' />
		</>,
		1.9
	);

/** A pane split off the left edge — the ticket list's keep-open / collapse toggle. */
export const SidebarGlyph: React.FC<GlyphProps> = ({ className, size = 16 }) =>
	stroke(
		size,
		className,
		<>
			<rect x='3' y='4' width='18' height='16' rx='2' />
			<path d='M9.5 4v16' />
		</>,
		1.9
	);

export const SearchGlyph: React.FC<GlyphProps> = ({ className, size = 16 }) =>
	stroke(
		size,
		className,
		<>
			<circle cx='11' cy='11' r='7' />
			<line x1='21' y1='21' x2='16.65' y2='16.65' />
		</>
	);

/** Tapering horizontal lines — the Search criteria toggle beside the search box. */
export const FilterGlyph: React.FC<StrokeGlyphProps> = ({ className, size = 17, strokeWidth = 1.9 }) =>
	stroke(
		size,
		className,
		<>
			<line x1='4' y1='6' x2='20' y2='6' />
			<line x1='7' y1='12' x2='17' y2='12' />
			<line x1='10' y1='18' x2='14' y2='18' />
		</>,
		strokeWidth
	);

export const SpamGlyph: React.FC<GlyphProps> = ({ className, size = 16 }) =>
	stroke(
		size,
		className,
		<>
			<path d='M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' />
			<path d='M12 9v4M12 17h.01' />
		</>
	);

export const ArchiveGlyph: React.FC<GlyphProps> = ({ className, size = 16 }) =>
	stroke(
		size,
		className,
		<>
			<rect x='3' y='4' width='18' height='5' rx='1' />
			<path d='M5 9v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9M10 13h4' />
		</>
	);

/** Arrow exiting a tray — the Forward action in the ticket top bar. */
export const DownloadGlyph: React.FC<GlyphProps> = ({ className, size = 14 }) =>
	stroke(
		size,
		className,
		<>
			<path d='M12 3v12' />
			<path d='m7 10 5 5 5-5' />
			<path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
		</>
	);

export const PlayGlyph: React.FC<GlyphProps> = ({ className, size = 14 }) =>
	stroke(size, className, <path d='M7 4.5v15l12-7.5z' />);

export const ForwardGlyph: React.FC<GlyphProps> = ({ className, size = 18 }) =>
	stroke(
		size,
		className,
		<>
			<path d='M15 17l5-5-5-5' />
			<path d='M4 18v-2a4 4 0 0 1 4-4h12' />
		</>,
		1.8
	);

export const EyeGlyph: React.FC<GlyphProps> = ({ className, size = 18 }) =>
	stroke(
		size,
		className,
		<>
			<path d='M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z' />
			<circle cx='12' cy='12' r='3' />
		</>,
		1.8
	);

export const EyeOffGlyph: React.FC<GlyphProps> = ({ className, size = 18 }) =>
	stroke(
		size,
		className,
		<>
			<path d='M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z' />
			<circle cx='12' cy='12' r='3' />
			<path d='M4 4l16 16' />
		</>,
		1.8
	);

export const PersonSwapGlyph: React.FC<GlyphProps> = ({ className, size = 18 }) =>
	stroke(
		size,
		className,
		<>
			<circle cx='8' cy='6' r='3.5' />
			<path d='M3 20a5 5 0 0 1 10 0' />
			<path d='M15 7h7' />
			<path d='m19.5 4.5 2.5 2.5-2.5 2.5' />
			<path d='M22 16h-7' />
			<path d='m17.5 13.5-2.5 2.5 2.5 2.5' />
		</>,
		1.8
	);

/**
 * The GitHub "Octocat" mark. Unlike the other glyphs this is a filled (not stroked) icon, so it
 * tints via `fill='currentColor'` and ignores the shared stroke helper.
 */
export const GitHubGlyph: React.FC<GlyphProps> = ({ className, size = 22 }) => (
	<svg width={size} height={size} viewBox='0 0 24 24' fill='currentColor' className={className} aria-hidden='true'>
		<path d='M12 .5a12 12 0 0 0-3.8 23.4c.6.1.8-.3.8-.6v-2c-3.3.7-4-1.6-4-1.6-.6-1.4-1.3-1.8-1.3-1.8-1.1-.7 0-.7 0-.7 1.2 0 1.8 1.2 1.8 1.2 1 1.8 2.8 1.3 3.5 1 0-.8.4-1.3.7-1.6-2.7-.3-5.5-1.3-5.5-5.9 0-1.3.5-2.4 1.2-3.2 0-.3-.5-1.5.2-3.2 0 0 1-.3 3.3 1.2a11.5 11.5 0 0 1 6 0C17 4.6 18 4.9 18 4.9c.7 1.7.2 2.9.1 3.2.8.8 1.2 1.9 1.2 3.2 0 4.6-2.8 5.6-5.5 5.9.4.4.8 1.1.8 2.2v3.3c0 .3.2.7.8.6A12 12 0 0 0 12 .5z' />
	</svg>
);
